import React, { useContext, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Stack, Typography } from "@mui/material";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";
import useContactPersonDetails from "../../hooks/contactPersons/useContactPersonDetails";
import useCompanies from "../../hooks/companies/useCompanies";
import useMembers from "../../hooks/members/useMembers";
import dayjs from "dayjs";
import ContactPersonForm, { ContactPersonFormValues } from "../../components/contactpersons/ContactPersonForm";
import { updateContactPerson } from "../../api/contactPersons";
import { CreateContactPersonDto, CrmContactStatus } from "../../types/companyTypes";

const EditContactPerson: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const contactPersonId = Number(id);
  const { contactPersonDetails } = useContactPersonDetails(contactPersonId);
  const { allCompanies } = useCompanies();
  const { members } = useMembers();

  const { auth } = useContext(AuthContext);
  const hasCrmPermissions = doesPermissionsHaveSomeOf(auth.permissions, [13]);
  const isJbtContact =
    contactPersonDetails != null &&
    contactPersonDetails.jbtContactId != null &&
    contactPersonDetails.jbtContactId === auth.userID;
  const canEditContactPerson = hasCrmPermissions || isJbtContact;

  const initialValues = useMemo<ContactPersonFormValues | undefined>(() => {
    // The form autocompletes need full member/company objects, so the edit state is derived only once all sources are loaded.
    if (!contactPersonDetails || members.length === 0 || allCompanies.length === 0) {
      return undefined;
    }
    const member = contactPersonDetails.jbtContactId
      ? members.find((m) => m.memberId === contactPersonDetails.jbtContactId)
      : undefined;
    const birthday = contactPersonDetails.birthday ? dayjs(contactPersonDetails.birthday) : null;
    return {
      name: contactPersonDetails.name || "",
      salutation: contactPersonDetails.salutation || "",
      position: contactPersonDetails.position || "",
      mobilePhone: contactPersonDetails.mobilePhone || "",
      email: contactPersonDetails.email || "",
      birthday,
      yearUnknown: birthday?.year() === 0,
      selectedCompany: allCompanies.find((c) => c.companyId === contactPersonDetails.companyId) || null,
      selectedJbtContact: member
        ? { memberId: member.memberId, firstname: member.firstname, lastname: member.lastname }
        : null,
      crmContactPreference:
        contactPersonDetails.crmContactAllowed === false ||
        contactPersonDetails.crmContactStatus === CrmContactStatus.NO_FURTHER_CONTACT_DESIRED
          ? CrmContactStatus.NO_FURTHER_CONTACT_DESIRED
          : contactPersonDetails.crmContactStatus === CrmContactStatus.POTENTIAL_FUTURE_PROJECT
          ? CrmContactStatus.POTENTIAL_FUTURE_PROJECT
          : CrmContactStatus.ACTIVE,
      crmContactStatusReason: contactPersonDetails.crmContactStatusReason || "",
    };
  }, [contactPersonDetails, members, allCompanies]);

  if (!initialValues) {
    return <div />;
  }

  if (!canEditContactPerson) {
    return (
      <Container>
        <Stack>
          <Typography variant="h5" fontWeight={"bold"}>
            Keine Berechtigung
          </Typography>
          <Typography variant="body1">Du hast keine Berechtigung diese Ansprechperson zu bearbeiten.</Typography>
        </Stack>
      </Container>
    );
  }

  const handleSubmit = async (data: CreateContactPersonDto) => {
    await updateContactPerson(contactPersonId, data);
    navigate(`/ansprechpersonen/${contactPersonId}`);
  };

  return (
    <ContactPersonForm
      title="Ansprechperson bearbeiten"
      submitLabel="Speichern"
      cancelHref={`/ansprechpersonen/${id}`}
      dialogIntro="Die Ansprechperson wird folgendermaßen bearbeitet:"
      dialogActorLabel="Geändert von"
      initialValues={initialValues}
      showCrmContactFields={hasCrmPermissions}
      onSubmit={handleSubmit}
    />
  );
};

export default EditContactPerson;
